import React from 'react';
import { Button } from '../../../../components/ui/button';
import { Loader2, Clock as ClockIcon, RefreshCw } from 'lucide-react';

export interface ManualMachineLocationDisplay {
  station: string;
  bugType: string;
  bugNum: string;
}

interface ManualWeldingMachineStatusRowProps {
  machineLabel: string;
  connected: boolean;
  weldUID?: string;
  weldTypeLabel?: string;
  /** Optional Reset MQTT control (per machine, same as Set Time targeting). */
  onResetMqtt?: () => void;
  isResetMqttLoading?: boolean;
  resetMqttDisabled?: boolean;
}

export const ManualWeldingMachineStatusRow: React.FC<ManualWeldingMachineStatusRowProps> = ({
  machineLabel,
  connected,
  weldUID,
  weldTypeLabel,
  onResetMqtt,
  isResetMqttLoading = false,
  resetMqttDisabled = false,
}) => (
  <div className="flex flex-col gap-2 p-3 bg-page-bg border border-[#E2E5EA] rounded-lg sm:flex-row sm:items-center sm:justify-between">
    <p className="text-[13px] font-medium text-text-grey shrink-0">{machineLabel}</p>
    <div className="flex flex-wrap items-center gap-2 text-[13px] min-w-0 sm:justify-end">
      {connected ? (
        <>
          <span className="h-1.5 w-1.5 bg-hero-red rounded-full" />
          <span className="text-text-grey">{weldTypeLabel || 'Manual'}</span>
          {weldUID && (
            <span className="text-[12px] font-medium text-galvanizing-green">UID: {weldUID}</span>
          )}
        </>
      ) : (
        <>
          <span className="h-1.5 w-1.5 bg-[#B38600] rounded-full" />
          <span className="text-[#B38600]">Welding Machine not connected</span>
        </>
      )}
      {onResetMqtt && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onResetMqtt}
          disabled={resetMqttDisabled || isResetMqttLoading}
          className="border-[#E2E5EA] text-text-grey hover:bg-white"
        >
          {isResetMqttLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Resetting...
            </>
          ) : (
            <>
              <RefreshCw className="w-4 h-4 mr-2" />
              Reset MQTT
            </>
          )}
        </Button>
      )}
    </div>
  </div>
);

interface ManualWeldingMachineTimeSectionProps {
  machineLabel: string;
  timeDisplay: string | null;
  connected: boolean;
  isTimeLoading: boolean;
  isSetTimeLoading: boolean;
  onGetTime: () => void;
  onSetTime: () => void;
}

export const ManualWeldingMachineTimeSection: React.FC<ManualWeldingMachineTimeSectionProps> = ({
  machineLabel,
  timeDisplay,
  connected,
  isTimeLoading,
  isSetTimeLoading,
  onGetTime,
  onSetTime,
}) => (
  <div className="h-full rounded-lg border border-[#E2E5EA] bg-white p-4 space-y-4">
    <div className="text-[12px] font-semibold text-blueprint-blue">{machineLabel}</div>
    <div className="bg-page-bg border border-[#E2E5EA] rounded-lg p-4 text-center min-h-[88px] flex flex-col items-center justify-center">
      <div className="text-[11px] text-heritage-grey mb-1.5">Device Time</div>
      {timeDisplay ? (
        <div className="text-[16px] font-semibold text-blueprint-blue">{timeDisplay}</div>
      ) : (
        <div className="inline-flex items-center gap-2 text-[13px] text-heritage-grey">
          <ClockIcon className="h-4 w-4 shrink-0" />
          <span>
            Click <span className="font-medium text-text-grey">Get Time</span> to retrieve device time.
          </span>
        </div>
      )}
    </div>
    <div className="grid grid-cols-2 gap-3">
      <Button
        size="sm"
        onClick={onGetTime}
        disabled={!connected || isTimeLoading || isSetTimeLoading}
        className="bg-hero-red hover:bg-hero-red-60 text-white"
      >
        {isTimeLoading ? (
          <>
            <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />
            Getting...
          </>
        ) : (
          'Get Time'
        )}
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={onSetTime}
        disabled={!connected || isTimeLoading || isSetTimeLoading}
        className="border-[#E2E5EA] text-text-grey hover:bg-page-bg"
      >
        {isSetTimeLoading ? (
          <>
            <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />
            Syncing...
          </>
        ) : (
          'Sync Now'
        )}
      </Button>
    </div>
  </div>
);

interface ManualWeldingMachineLogsSectionProps {
  machineLabel: string;
  logCount: string | number;
  location: ManualMachineLocationDisplay;
  connected: boolean;
  isLogsLoading: boolean;
  isEraseLoading: boolean;
  isLocationLoading: boolean;
  erasing: boolean;
  onCheckLogs: () => void;
  onEraseLogs: () => void;
  onLocation: () => void;
}

export const ManualWeldingMachineLogsSection: React.FC<ManualWeldingMachineLogsSectionProps> = ({
  machineLabel,
  logCount,
  location,
  connected,
  isLogsLoading,
  isEraseLoading,
  isLocationLoading,
  erasing,
  onCheckLogs,
  onEraseLogs,
  onLocation,
}) => (
  <div className="h-full rounded-lg border border-[#E2E5EA] bg-white p-4 space-y-4">
    <div className="text-[12px] font-semibold text-blueprint-blue">{machineLabel}</div>
    <div className="grid grid-cols-1 gap-4">
      <div className="bg-page-bg border border-[#E2E5EA] rounded-lg p-3 text-center flex flex-col justify-center min-h-[72px]">
        <div className="text-[11px] text-heritage-grey mb-1.5">Active Logs</div>
        {!isLogsLoading ? (
          <div className="text-[20px] font-semibold text-blueprint-blue">{logCount}</div>
        ) : (
          <div className="text-[14px] font-medium text-heritage-grey">—</div>
        )}
      </div>
      <div className="bg-page-bg border border-[#E2E5EA] rounded-lg p-3 min-h-[96px]">
        <div className="text-[11px] text-heritage-grey mb-1.5">Device Location</div>
        <div className="space-y-0.5 text-[12px]">
          <div>
            <span className="text-heritage-grey">Station:</span>{' '}
            <span className="font-medium text-text-grey">{location.station}</span>
          </div>
          <div>
            <span className="text-heritage-grey">Bug Type:</span>{' '}
            <span className="font-medium text-text-grey">{location.bugType}</span>
          </div>
          <div>
            <span className="text-heritage-grey">Bug Number:</span>{' '}
            <span className="font-medium text-text-grey">{location.bugNum}</span>
          </div>
        </div>
      </div>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      <Button
        variant="outline"
        size="sm"
        onClick={onCheckLogs}
        disabled={!connected || isLogsLoading || isEraseLoading || isLocationLoading}
        className="w-full border-[#E2E5EA] text-text-grey hover:bg-page-bg text-[12px]"
      >
        {isLogsLoading ? (
          <>
            <Loader2 className="h-3 w-3 animate-spin mr-1" />
            Reading...
          </>
        ) : (
          'Read Log Count'
        )}
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={onEraseLogs}
        disabled={!connected || erasing || isLogsLoading || isEraseLoading || isLocationLoading}
        className="w-full border-[rgba(235,28,55,0.2)] text-hero-red hover:text-hero-red hover:bg-[rgba(235,28,55,0.06)] text-[12px]"
      >
        {isEraseLoading ? (
          <>
            <Loader2 className="h-3 w-3 animate-spin mr-1" />
            Erasing...
          </>
        ) : (
          'Erase logs'
        )}
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={onLocation}
        disabled={!connected || isLogsLoading || isEraseLoading || isLocationLoading}
        className="w-full border-[#E2E5EA] text-text-grey hover:bg-page-bg text-[12px]"
      >
        {isLocationLoading ? (
          <>
            <Loader2 className="h-3 w-3 animate-spin mr-1" />
            Getting...
          </>
        ) : (
          'Get Location'
        )}
      </Button>
    </div>
  </div>
);
